<template>
  <div class="switcher" ref="langRef" v-if="currentLocale">
    <div
        class="switcher__button"
        @click="setSwitcherVisible(!isSwitcherVisible)"
        :class="[{inFooter: inFooter}]"
    >
      <p>{{ currentLocale.code.split('-')[0] }}</p>
      <svg :class="[{ active: isSwitcherVisible }]" width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.05 10L8.04999 6L4.04999 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <div class="switcher__menu" :class="[{active: isSwitcherVisible}, {inFooter: inFooter}]">
      <div class="switcher__menu-wrapper">
        <p
            v-for="locale of locales"
            :key="locale.code"
            @click="switchLocale(locale.code)"
            class="switcher__menu-button"
        >
          {{ locale.name.replace(/\s*\([^)]*\)\s*/g, '') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import translations from "@/core/helpers/translations.js";
import {useRoute, useRouter} from "vue-router";
import { onClickOutside } from '@vueuse/core'
import {Languages} from "@/core/api/languages.js";
import translation from "@/core/helpers/translations.js";
import {useAuthStore} from "@/stores/auth.js";
import {Auth} from "@/core/api/auth.js";

const props = defineProps({
  inFooter: Boolean
})

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const userUuid = computed(() => {
  return authStore.user ? authStore.user.uuid : ''
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})

const locales = ref([])

const isSwitcherVisible = ref(false)
const setSwitcherVisible = (state) => {
  isSwitcherVisible.value = state
}

const langRef = ref(null)
onClickOutside(langRef, () => isSwitcherVisible.value = false)

const currentLocale = ref(null)

const getLanguages = async () => {
  const res = await Languages.getLanguages()
  locales.value = res.data?.languages.filter((locale) => translation.supportedLocales.includes(locale.code));
  currentLocale.value = locales.value.find((locale) => locale.code === translation.currentLocale)
}

const switchLocale = async (newLocale) => {
  setSwitcherVisible(false)

  if (userUuid.value && accessToken.value) {
    const res = await Auth.setLanguage(userUuid.value, accessToken.value, newLocale)

    if (!res.errors) {
      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })

      if (newLocale !== translation.currentLocale) {
        translations.switchLanguage(newLocale)
      }
    }
  } else {
    translations.switchLanguage(newLocale)
  }

  await router.push({
    name: route.name,
    params: {
      ...route.params,
      locale: newLocale
    },
    query: { ...route.query }
  })
}

watch (
  () => translation.currentLocale,
  () => {
    currentLocale.value = locales.value.find((locale) => locale.code === localStorage.getItem('ngs-user-locale'))
  }
)

onMounted(async () => {
  await getLanguages()
})
</script>

<style lang="scss" scoped>
.switcher {
  position: relative;
  z-index: 3;

  &__button {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    border-radius: 50px;
    background-color: #F6F7F8;
    padding: 5px 10px;
    transition: 0.2s;

    color: #5A5A5A;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    text-transform: uppercase;

    &.inFooter {
      border: 1px solid #FF6162;
      background-color: transparent;
      color: #FF6162;

      @include hover {
        background-color: #363636;
      }

      & svg {
        & path {
          color: #FF6162;
        }
      }
    }

    @include hover {
      background-color: #e8e8e8;
    }

    & svg {
      transition: 0.2s;
      transform: rotate(180deg);

      &.active {
        transform: rotate(0deg);
      }

      & path {
        color: #5A5A5A;
      }
    }
  }

  &__menu {
    position: absolute;
    z-index: 3;
    top: 150%;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 12px;
    background-color: $white;
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.2s ease;
    overflow: hidden;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

    &.active {
      grid-template-rows: 1fr;
    }

    &.inFooter {
      top: unset;
      bottom: 150%;
      background-color: #1C1C1C;

      & div {
        gap: 5px;
      }

      & p {
        background-color: transparent;
        border: 1px solid #FF6162;
        padding-block: 5px;
        color: #FF6162;

        @include hover {
          background-color: #363636;
        }
      }
    }

    & > * {
      min-height: 0;
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
    }

    &-button {
      cursor: pointer;
      padding: 7px 15px;
      background-color: $white;
      border-radius: 8px;
      margin-inline: 7px;
      transition: 0.2s;

      color: #2F2F2F;
      font-size: 16px;
      font-weight: 400;
      line-height: 19.2px;

      &:first-child {
        margin-top: 7px;
      }
      &:last-child {
        margin-bottom: 7px;
      }

      @include hover {
        background-color: #EDEFF1;
      }
    }
  }
}
</style>